/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { Alert } from 'antd'
import { Formik, useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type {
  TeamPlayerFinderFormValues,
  TeamPlayerFinderRegistration,
} from '@sportninja/common/features/team/playerFinder/types'
import { playerFinderPositionOptions } from '@sportninja/common/features/team/playerFinder/utils/positionOptions'
import { optInBlockerExplanations } from '@sportninja/common/features/team/playerFinder/utils/reasonKeys'
import { playerFinderRegistrationLabel } from '@sportninja/common/features/team/playerFinder/utils/registrationLabel'
import {
  canChangePositions,
  canChangeRecruiting,
  hasPlayerFinderChanges,
  isWithdrawOnly,
  positionsOwnedBySlots,
  toPlayerFinderFormValues,
} from '@sportninja/common/features/team/playerFinder/utils/settingsForm'
import Button from 'src/components/ts-components/Button'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'
import SelectMultiInput from 'src/components/ts-components/SelectMultiInput'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import Text from 'src/components/ts-components/Text'
import {
  balanceLabel,
  balanceText,
} from 'src/features/team-balance/utils/balanceI18n'
import useAdoptFreshSettings from '../hooks/useAdoptFreshSettings'
import useRecruitingSettingsSave, {
  RecruitingSettingsSave,
} from '../hooks/useRecruitingSettingsSave'
import PlayerFinderVisibility from './PlayerFinderVisibility'

interface RecruitingSettingsCardProps {
  entry: TeamPlayerFinderRegistration
  teamId: string
  authUserId?: string | number | null
  /**
   * Switches to the Team Budget sub-tab, where listed slots own their positions.
   * Absent when there is no Team Budget sub-tab — the link is then omitted.
   */
  onOpenTeamBudget?: () => void
}

/**
 * UI option 1A — ONE card for the selected registration, with an explicit Save:
 * the "Looking for players" switch (the team's master switch in this
 * competition), Positions Needed, and whether search shows the team right now.
 *
 * The parent keys this card by registration, so switching competitions starts a
 * fresh form rather than carrying one registration's unsaved choice to another.
 */
const RecruitingSettingsCard: React.FC<RecruitingSettingsCardProps> = ({
  entry,
  teamId,
  authUserId,
  onOpenTeamBudget,
}) => {
  const save = useRecruitingSettingsSave({ entry, teamId, authUserId })

  return (
    <Formik<TeamPlayerFinderFormValues>
      initialValues={toPlayerFinderFormValues(entry)}
      onSubmit={save.submit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <RecruitingSettingsForm
        entry={entry}
        save={save}
        onOpenTeamBudget={onOpenTeamBudget}
      />
    </Formik>
  )
}

export default RecruitingSettingsCard

interface RecruitingSettingsFormProps {
  entry: TeamPlayerFinderRegistration
  save: RecruitingSettingsSave
  onOpenTeamBudget?: () => void
}

const RecruitingSettingsForm: React.FC<RecruitingSettingsFormProps> = ({
  entry,
  save,
  onOpenTeamBudget,
}) => {
  const { values, errors, touched, setFieldValue, setFieldError, submitForm } =
    useFormikContext<TeamPlayerFinderFormValues>()
  useAdoptFreshSettings(entry, save)

  const withdrawOnly = isWithdrawOnly(entry)
  const slotOwned = positionsOwnedBySlots(entry)
  const blockers =
    entry.can_manage && !entry.recruiting && !entry.can_opt_in
      ? optInBlockerExplanations(entry.opt_in_blockers, entry.blocking_division)
      : []

  // Errors show only for a field the server named, and clear as it is edited.
  const errorFor = (name: keyof TeamPlayerFinderFormValues) =>
    touched[name] && errors[name] ? { [name]: String(errors[name]) } : {}

  const setValue = <K extends keyof TeamPlayerFinderFormValues>(
    name: K,
    value: TeamPlayerFinderFormValues[K]
  ) => {
    setFieldValue(name, value)
    if (errors[name]) {
      setFieldError(name, undefined)
    }
  }

  return (
    <div css={cardStyle} data-testid='recruiting-settings-card'>
      <div css={headerStyle}>
        <Text variant='heading6' weight='semiBold' color={colors.NEUTRAL_0}>
          {playerFinderRegistrationLabel(entry)}
        </Text>
        <PlayerFinderVisibility entry={entry} />
      </div>

      {save.conflict ? (
        <Alert
          type='warning'
          showIcon
          data-testid='player-finder-conflict'
          message={balanceText('common:teamPlayerFinderConflict')}
        />
      ) : null}

      {withdrawOnly ? (
        <Alert
          type='warning'
          showIcon
          data-testid='player-finder-withdraw-only'
          message={balanceText('common:teamPlayerFinderWithdrawOnly')}
        />
      ) : null}

      {entry.can_manage ? null : (
        <Alert
          type='info'
          showIcon
          data-testid='player-finder-read-only'
          message={
            entry.blocking_division
              ? balanceText('common:teamPlayerFinderManagedBy', {
                  division: entry.blocking_division,
                })
              : balanceText('common:teamPlayerFinderManagedElsewhere')
          }
        />
      )}

      <div css={fieldStyle}>
        <div css={switchRowStyle}>
          <SwitchInput
            title={balanceLabel('common:teamPlayerFinderRecruitingLabel')}
            subtitle={balanceText('common:teamPlayerFinderRecruitingHint')}
            value={values.recruiting}
            onChange={(next) => setValue('recruiting', next)}
            disabled={!canChangeRecruiting(entry) || save.isSaving}
            dataTestId='player-finder-recruiting'
          />
        </div>
        <ErrorLabel
          fieldErrors={errorFor('recruiting')}
          customError={{}}
          name='recruiting'
        />
        {blockers.length > 0 ? (
          <div css={blockersStyle} data-testid='player-finder-blockers'>
            {blockers.map((blocker) => (
              <Text
                key={blocker.key}
                variant='paragraphXSmall'
                color={colors.NEUTRAL_70}
              >
                {balanceText(blocker.key, blocker.params)}
              </Text>
            ))}
          </div>
        ) : null}
      </div>

      {withdrawOnly ? null : (
        <div css={fieldStyle}>
          <SelectMultiInput
            label={balanceLabel('common:teamPlayerFinderPositionsLabel')}
            name='positionIds'
            options={playerFinderPositionOptions(entry)}
            value={values.positionIds}
            onChange={(ids) => setValue('positionIds', ids)}
            fieldErrors={errorFor('positionIds')}
            customError={{}}
            placeholder={capitalizeWords(
              balanceText('common:teamPlayerFinderPositionsAny')
            )}
            disabled={!canChangePositions(entry) || save.isSaving}
          />
          {slotOwned ? (
            <div css={slotNoteStyle} data-testid='player-finder-slot-owned'>
              <Text variant='paragraphXSmall' color={colors.NEUTRAL_70}>
                {balanceText('common:teamPlayerFinderPositionsFromSlots')}
              </Text>
              {onOpenTeamBudget ? (
                <Button
                  text={balanceLabel(
                    'common:teamPlayerFinderManageInTeamBudget'
                  )}
                  variant='secondary'
                  onClick={onOpenTeamBudget}
                  style={inlineButtonStyle}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {entry.can_manage ? (
        <div css={footerStyle}>
          <Button
            text={balanceLabel('common:teamPlayerFinderSave')}
            variant='primary'
            onClick={submitForm}
            disabled={!hasPlayerFinderChanges(entry, values) || save.isSaving}
            isLoading={save.isSaving}
            style={inlineButtonStyle}
          />
        </div>
      ) : null}
    </div>
  )
}

// ---- styled ----
const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background: ${colors.SECONDARY_300};
`

const headerStyle = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
`

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

// SwitchInput renders its title and switch as siblings with no wrapper; this row
// keeps them on one line with the switch at the end.
const switchRowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const blockersStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const slotNoteStyle = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
`

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
`

// The shared Button stretches (`flex: 1 0 0`); these size to their label.
const inlineButtonStyle = css`
  flex: 0 0 auto;
`
